/**
 * shopifyFetch.js — talking to our API from inside the Shopify admin.
 *
 * App Bridge v4 (the CDN script in index.html) wraps window.fetch and attaches
 * a session token to same-origin requests itself — but only where the request
 * does not already carry an Authorization header. That matters, because it is
 * the whole of the following bug.
 *
 * This file used to cache a token and set that header on every request, which
 * took App Bridge out of the loop and left our own copy in charge. A Shopify
 * session token lives 60 seconds. The cache handed it out until 10 seconds
 * before expiry, so a request leaving at the 50-second mark, or any hiccup
 * between leaving and arriving, was rejected with "Authentication failed: Token
 * expired" — and the seller saw that on a publish. The give-away was that pages
 * calling plain fetch() never failed, because App Bridge was attaching a fresh
 * token for them, while everything going through apiFetch failed regularly.
 *
 * Renewing the token was also on the critical path. Once a minute a navigation
 * would block on the App Bridge handshake, with no timeout, which is where the
 * ten-to-twenty-second waits came from: the page awaited a token before it
 * would fetch anything at all.
 *
 * So: App Bridge attaches the token. We send no Authorization header when it is
 * present, ask for a token only where it is not, and retry once on a 401 with a
 * freshly minted token — an expired token is then something the app recovers
 * from rather than something the seller reads.
 */

import { adminImpersonationToken } from './adminSession'

/** True when App Bridge is here and will attach a token for us. */
const appBridgeManagesAuth = () =>
  typeof window !== 'undefined' && typeof window.shopify?.idToken === 'function'

/**
 * A token, for the cases App Bridge does not cover: admin impersonation, and
 * the retry after a 401.
 *
 * Concurrent callers share one handshake. Eight components each asking on the
 * same page load produced eight handshakes, serialised by App Bridge.
 *
 * Not cached. App Bridge keeps its own fresh copy, and a second cache here is
 * what caused the expiry in the first place.
 */
let inFlight = null

async function freshToken() {
  // One resolver, and it also looks in localStorage: a portal opened in a new
  // tab does not inherit sessionStorage. See utils/adminSession.
  const adminToken = adminImpersonationToken()
  if (adminToken) return adminToken

  if (!appBridgeManagesAuth()) return null
  if (inFlight) return inFlight

  inFlight = (async () => {
    try {
      // A stalled handshake must not hold a page open indefinitely. Without a
      // token the request still goes out, and App Bridge may well attach one.
      return await Promise.race([
        window.shopify.idToken(),
        new Promise(resolve => setTimeout(() => resolve(null), 8000)),
      ])
    } catch (e) {
      console.warn('[shopifyFetch] could not get a session token:', e.message)
      return null
    } finally {
      inFlight = null
    }
  })()
  return inFlight
}

/**
 * Headers for a request App Bridge is not attaching a token to.
 *
 * Empty where App Bridge is in charge: setting Authorization ourselves is what
 * stopped it attaching a fresh one.
 */
export async function getAuthHeaders() {
  // One resolver, and it also looks in localStorage: a portal opened in a new
  // tab does not inherit sessionStorage. See utils/adminSession.
  const adminToken = adminImpersonationToken()
  if (adminToken) return { Authorization: `Bearer ${adminToken}` }
  if (appBridgeManagesAuth()) return {}
  const token = await freshToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/** A 401 that is about the token rather than about the shop. */
const isAuthFailure = (status, data) =>
  status === 401 && /token|unauthorized|authentication/i.test(String(data?.error || ''))

/**
 * Waiting out a 429 instead of reporting it.
 *
 * The server limits how many calls a shop may make. When that trips it trips
 * for everything at once, because the calls a page makes go out together — so
 * a seller did not see one slow card, they saw the whole app empty: no
 * products, no settings, no plan, all in the same instant. It reads as data
 * loss, and that is how it was reported.
 *
 * A rate limit is the one failure where trying again shortly is the correct
 * response and the caller has nothing to decide, so it is handled here rather
 * than by every screen. The server sends Retry-After; it is believed up to a
 * point, because a header asking for a two-minute wait would hang the page.
 *
 * Only twice. A limit that is still refusing after two waits is not a burst,
 * and at that point the seller should be told rather than left watching a
 * spinner.
 */
const RETRY_LIMIT = 2
const sleep = ms => new Promise(r => setTimeout(r, ms))

function retryAfterMs(res, attempt) {
  const header = Number(res?.headers?.get?.('retry-after'))
  if (Number.isFinite(header) && header > 0) return Math.min(header * 1000, 5000)
  return 400 * Math.pow(2, attempt)   // 400ms, then 800ms
}

export function apiUrl(path, shop) {
  return `/api${path}${shop ? (path.includes('?') ? '&' : '?') + 'shop=' + shop : ''}`
}

/**
 * fetch for a raw response, with the same auth handling as apiFetch.
 *
 * For callers that want the Response itself rather than parsed JSON. The 401
 * retry matters just as much here: a page whose data silently failed to load
 * reads as an empty shop.
 */
export async function authedFetch(url, options = {}) {
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) }
  // no-store, because Express sets an ETag and the browser answers a repeat GET
  // with 304 Not Modified — which has an empty body. Callers then call .json()
  // on nothing, it throws, the catch swallows it, and the card stays blank
  // forever. That is what "the Data tab will not load" was: the requests were
  // reaching the server and coming back 304 with nothing in them.
  //
  // These are live figures anyway. Serving a cached copy of a seller's stock
  // levels is not something to want.
  const init = { cache: 'no-store', ...options, headers: { ...headers, ...(await getAuthHeaders()) } }

  let res = await fetch(url, init)
  for (let i = 0; res.status === 429 && i < RETRY_LIMIT; i++) {
    await sleep(retryAfterMs(res, i))
    res = await fetch(url, init)
  }
  if (res.status !== 401) return res

  const token = await freshToken()
  if (!token) return res
  return fetch(url, { ...init, headers: { ...headers, Authorization: `Bearer ${token}` } })
}

/**
 * The JSON body of a response, or {} when there is none.
 *
 * A 204 or a 304 carries no body at all, and .json() on an empty body throws —
 * which reads as "the data failed to load" while the request itself succeeded.
 */
export async function readJson(res) {
  if (!res || res.status === 204 || res.status === 304) return {}
  const raw = await res.text().catch(() => '')
  if (!raw) return {}
  try { return JSON.parse(raw) } catch { return {} }
}

export async function apiFetch(method, path, shop, body, signal) {
  const send = async extraHeaders => {
    const res = await fetch(apiUrl(path, shop), {
      method,
      headers: { 'Content-Type': 'application/json', ...extraHeaders },
      ...(body   ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal }                     : {}),
    })
    // A gateway timeout or nginx error returns an HTML page, not JSON. Parsing
    // that blind surfaced as "Unexpected token '<'", which told the user nothing.
    const raw = await res.text()
    let data
    try {
      data = raw ? JSON.parse(raw) : {}
    } catch {
      if (res.status === 504 || res.status === 502) {
        throw new Error('The request timed out. eBay is taking longer than usual — please try again.')
      }
      throw new Error(`Server returned an unexpected response (${res.status}). Please try again.`)
    }
    return { res, data }
  }

  const authHeaders = await getAuthHeaders()
  let { res, data } = await send(authHeaders)

  // Wait out a rate limit before anything else looks at the response, so a
  // burst never reaches a screen as a failure. See retryAfterMs.
  for (let i = 0; res.status === 429 && i < RETRY_LIMIT; i++) {
    await sleep(retryAfterMs(res, i))
    ;({ res, data } = await send(authHeaders))
  }

  // One retry with a token minted now. A token that expired in flight is the
  // app's problem to solve, not something to show a seller mid-publish.
  if (isAuthFailure(res.status, data)) {
    const token = await freshToken()
    if (token) ({ res, data } = await send({ Authorization: `Bearer ${token}` }))
  }

  if (!res.ok) {
    if (res.status === 402 && (data.requiresPlan || data.requiresUpgrade)) {
      const err = new Error(data.error || 'Subscription required')
      err.requiresPlan    = data.requiresPlan    || false
      err.requiresUpgrade = data.requiresUpgrade || false
      throw err
    }
    throw new Error(data.error || `Failed (${res.status})`)
  }
  return data
}
